import React, { ReactNode } from "react";
import { assetPath } from "@/utils/assetPath";
import { Drawer } from "./Drawer";

export type ModalSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface ModalProps {
  /** The content to be rendered inside the modal */
  children: ReactNode;

  /** The trigger button content */
  triggerContent?: ReactNode;

  /** Custom classes for the trigger button */
  triggerClassName?: string;

  /** Classes to be applied to the modal content */
  contentClassName?: string;

  /** Classes to be applied to the overlay/backdrop element */
  overlayClassName?: string;

  /** Control if modal is open or closed externally */
  open?: boolean;

  /** Handler for open state changes */
  onOpenChange?: (open: boolean) => void;

  /** Size of the modal */
  size?: ModalSize;

  /** Whether the modal should close when pressing the escape key */
  shouldCloseOnEscapePress?: boolean;

  /** Whether the modal should close when pressing the backdrop */
  shouldCloseOnOutsideClick?: boolean;

  /** Custom width override (will override size prop) */
  customWidth?: string;

  /** Custom max-width override (will override size prop) */
  customMaxWidth?: string;
}

/**
 * Predefined size configurations for the modal
 * - xs: 320px (w-80)
 * - sm: 384px (w-96) 
 * - md: 500px (w-[500px])
 * - lg: 600px (w-[600px])
 * - xl: 800px (w-[800px])
 */
const sizeClasses: Record<ModalSize, { width: string; maxWidth: string }> = {
  xs: { width: "w-80", maxWidth: "max-w-80" },
  sm: { width: "w-96", maxWidth: "max-w-96" },
  md: { width: "w-[500px]", maxWidth: "max-w-[500px]" },
  lg: { width: "w-[600px]", maxWidth: "max-w-[600px]" },
  xl: { width: "w-[800px]", maxWidth: "max-w-[800px]" },
};

/**
 * Modal Component
 * 
 * A center-positioned modal wrapper built on top of the Drawer component.
 * Provides consistent sizing, positioning, and behavior for modal dialogs.
 * 
 * @example
 * // Basic usage with trigger
 * <Modal 
 *   triggerContent={<button>Open Modal</button>}
 *   size="md"
 * >
 *   <ModalHeader title="My Modal" onClose={() => setOpen(false)} />
 *   <ModalContent>
 *     <p>Modal content goes here</p>
 *   </ModalContent>
 *   <ModalFooter>
 *     <button>Save</button>
 *   </ModalFooter>
 * </Modal>
 * 
 * @example
 * // Controlled usage
 * <Modal 
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   size="lg"
 * >
 *   <div>Custom content</div>
 * </Modal>
 * 
 * @example
 * // Custom size
 * <Modal 
 *   triggerContent={<button>Custom Size</button>}
 *   customWidth="w-[400px]"
 *   customMaxWidth="max-w-[400px]"
 * >
 *   <div>Content with custom width</div>
 * </Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  children,
  triggerContent,
  triggerClassName = "",
  contentClassName = "",
  overlayClassName = "",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  size = "md",
  customWidth,
  customMaxWidth,
}) => {
  const sizeConfig = sizeClasses[size];
  const widthClass = customWidth || sizeConfig.width;
  const maxWidthClass = customMaxWidth || sizeConfig.maxWidth;

  return (
    <Drawer
      triggerContent={triggerContent}
      triggerClassName={triggerClassName}
      open={controlledOpen}
      onOpenChange={controlledOnOpenChange}
      position="center"
      contentClassName={`${contentClassName} ${widthClass} ${maxWidthClass}`}
      overlayClassName={overlayClassName}
    >
      <div className="relative">
        {children}
      </div>
    </Drawer>
  );
};

/**
 * ModalHeader Component
 * 
 * A standardized header component for modals with title and optional close button.
 * 
 * @example
 * <ModalHeader 
 *   title="Edit Profile" 
 *   onClose={() => setOpen(false)}
 *   showCloseButton={true}
 * >
 *   <button>Help</button>
 * </ModalHeader>
 */
export const ModalHeader: React.FC<{
  title: string;
  onClose?: () => void;
  showCloseButton?: boolean;
  children?: ReactNode;
}> = ({ title, onClose, showCloseButton = true, children }) => (
  <div className="flex items-center justify-between pb-4 mb-4 border-b border-black-6">
    <h2 className="text-18 font-semibold">{title}</h2>
    <div className="flex items-center gap-2">
      {children}
      {showCloseButton && onClose && (
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <img
            src={assetPath('icons/x.svg')}
            alt="close"
            className="w-5 h-5"
          />
        </button>
      )}
    </div>
  </div>
);

/**
 * ModalContent Component
 * 
 * A content wrapper for modal body content with consistent spacing.
 * 
 * @example
 * <ModalContent>
 *   <p>This is the main content of the modal</p>
 *   <form>Form elements go here</form>
 * </ModalContent>
 */
export const ModalContent: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className={`flex flex-col gap-4 ${className}`}>
    {children}
  </div>
);

/**
 * ModalFooter Component
 * 
 * A standardized footer component for modals with consistent styling and spacing.
 * 
 * @example
 * <ModalFooter>
 *   <button className="btn-secondary">Cancel</button>
 *   <button className="btn-primary">Save Changes</button>
 * </ModalFooter>
 */
export const ModalFooter: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className={`flex flex-col gap-2 mt-6 pt-4 border-t border-black-6 ${className}`}>
    {children}
  </div>
);
